import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { FiEye } from "react-icons/fi";
import MainCard from "../../../component/MainCard/MainCard";
import Button from "../../../component/Buttons/Button";
import ModalComponents from "../../../component/ModalComponent/ModalComponent";
import * as actions from "../../../Redux/Actions";
import {
  showErrorToastMessage,
  showSuccessToastMessage,
} from "../../../component/Toasts/Toasts";
import hanldeUnauthorizedUser from "../../../component/AccessConditions/UnauthorizedUser";
import DevelopmentPlanModal from "./DevelopmentPlanModal";
import AssessCandidate from "./AssessCandidate";
import { downloadSuccessorReportPdf } from "../report/successorReportPdf";
import "../readable-form.scss";

const empName = (e: any) =>
  [e?.candidateName, e?.middleName, e?.lastname].filter(Boolean).join(" ").trim() || "-";

// The workbook stores Performance / Potential as numbers 1-3 (that's why the eye
// legend exists). HR still *chooses* High/Medium/Low in the Reassess form; the table
// shows the number. Post-development Performance is entered here directly as a number.
const LEVEL_SCORE: any = { High: 3, Medium: 2, Low: 1 };
const SCALE = [
  { n: 3, label: "High" },
  { n: 2, label: "Medium" },
  { n: 1, label: "Low" },
];

// number from either a level (High/Medium/Low) or a numeric value, valid only in 1-3
// (so a stray out-of-range value can't produce a box outside 1-9)
const toNum = (v: any): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = LEVEL_SCORE[v] ?? Number(v);
  return Number.isFinite(n) && n >= 1 && n <= 3 ? n : null;
};

// a valid post-development entry is blank or an integer 1-3
const validLevel = (v: string) => v === "" || [1, 2, 3].includes(Number(v));

// Box = (Potential x 3) - (3 - Performance) — the source workbook's own formula.
const boxFromNums = (perfN: number | null, potN: number | null) =>
  perfN && potN ? potN * 3 - (3 - perfN) : null;

// The workbook's Box -> Category legend (its M/N columns), labels corrected onto the
// right cells. Shown behind the eye button on Post-Development.
const BOX_CATEGORY: any = {
  9: "Future Leader", 8: "High Potential", 7: "Emerging Potential",
  6: "Strong Performer", 5: "Core Talent", 4: "Low Performer",
  3: "Specialist", 2: "Inconsistent", 1: "At Risk",
};
const LEGEND_ROWS = [9, 8, 7, 6, 5, 4, 3, 2, 1];

/**
 * One successor's development journey, in three tabs:
 *  - Pre-Development  : the succession assessment (view — assessed from Post-Development).
 *  - Development      : the development plan (gaps, actions, timeline, owner, status).
 *  - Post-Development : the Employee_Data-style results row — status, potential, and the
 *                       post-development Performance HR records here, with the box/category
 *                       legend behind the eye button, and the Reassess action.
 */
const CandidateDevelopment = () => {
  const { unAuthorizeUser } = hanldeUnauthorizedUser();
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [candidate, setCandidate] = useState<any>(null);
  const [plans, setPlans] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState<"pre" | "dev" | "post">("pre");
  const [showAssess, setShowAssess] = useState(false);
  const [showLegend, setShowLegend] = useState(false);
  // typed post-development numbers, saved with an explicit Save button
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [perfInput, setPerfInput] = useState("");
  const [potInput, setPotInput] = useState("");
  // progress feedback on the development plan — required before moving to post-development
  const [fbInput, setFbInput] = useState("");
  const [fbSaving, setFbSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const userLogin: any = localStorage.getItem("login");
  const loginId = JSON.parse(userLogin || "{}")?.id;

  // the id currently on screen; a response for a different id (fast A->B navigation)
  // is ignored so it can't overwrite state with stale data.
  const currentId = useRef<string | undefined>(id);

  const loadCandidate = () => {
    if (!id) return;
    const rid = id;
    // NOTE: `id` here is the CANDIDATE id (route /development-plan/:id), not a critical
    // role ref. getCandidatesByCriticalRoleAction takes a criticalRoleRef, so we pass
    // undefined (all mapped candidates) and find this one by _id below. Passing `id` as
    // the ref would query criticalRoleRef: <candidateId> and return nothing.
    dispatch(
      actions.getCandidatesByCriticalRoleAction(undefined, (res: any) => {
        if (rid !== currentId.current) return;
        if (res?.error) {
          showErrorToastMessage("Couldn't load this successor. Please retry.");
          setLoading(false);
          return;
        }
        if (res?.data) setCandidate(res.data.find((c: any) => c._id === id) || null);
        setLoading(false);
      })
    );
  };
  const loadPlans = () => {
    if (!id) return;
    const rid = id;
    dispatch(
      actions.getDevelopmentPlansAction(id, (res: any) => {
        if (rid !== currentId.current) return;
        if (res?.error) showErrorToastMessage("Couldn't load the development plan.");
        else if (res?.data) setPlans(res.data);
      })
    );
  };

  useEffect(() => {
    unAuthorizeUser();
    currentId.current = id;
    loadCandidate();
    loadPlans();
  }, [id]);

  // keep the inputs in sync when the candidate (re)loads
  useEffect(() => {
    setPerfInput(candidate?.postPerformance ?? "");
    setPotInput(candidate?.postPotential ?? "");
  }, [candidate?.postPerformance, candidate?.postPotential]);

  useEffect(() => {
    setFbInput(candidate?.developmentProgressFeedback ?? "");
  }, [candidate?.developmentProgressFeedback]);

  const emp = candidate?.employee;
  const seat = candidate?.criticalRole;
  const seatLabel = seat
    ? `${seat.designation?.title || "-"}${seat.client?.clientName ? ` — ${seat.client.clientName}` : ""}`
    : "";

  const val = (v: any) => (v ? v : <span className="muted">-</span>);
  const isAssessed = !!(candidate?.readiness || candidate?.performance || candidate?.potential || candidate?.riskOfLoss);

  // development status for the results row, derived from the plan items
  const devStatus = !plans.length
    ? "Not Started"
    : plans.every((p) => p.status === "Completed")
    ? "Completed"
    : plans.some((p) => p.status === "In Progress" || p.status === "Completed")
    ? "In Progress"
    : "Not Started";

  // numbers for the box: post values HR typed, falling back to the assessment.
  const potNum = toNum(candidate?.postPotential) ?? toNum(candidate?.potential);
  const perfNum = toNum(candidate?.postPerformance) ?? toNum(candidate?.performance);
  const postBox = boxFromNums(perfNum, potNum);

  const startEdit = () => {
    setPotInput(candidate?.postPotential ?? "");
    setPerfInput(candidate?.postPerformance ?? "");
    setEditing(true);
  };
  const cancelEdit = () => {
    setPotInput(candidate?.postPotential ?? "");
    setPerfInput(candidate?.postPerformance ?? "");
    setEditing(false);
  };

  // download the full per-successor PDF report
  const downloadReport = () => {
    if (!id || downloading) return;
    setDownloading(true);
    dispatch(
      actions.getSuccessorReportAction(id, (res: any) => {
        setDownloading(false);
        if (res?.error || !res?.data) {
          showErrorToastMessage("Couldn't build the report. Please retry.");
          return;
        }
        try {
          downloadSuccessorReportPdf(res.data);
        } catch (e) {
          showErrorToastMessage("Couldn't generate the PDF.");
        }
      })
    );
  };

  // whether a progress note has been saved — the close-gate to post-development
  const hasFeedback = !!(candidate?.developmentProgressFeedback || "").trim();

  // save the progress feedback note on the development plan
  const saveFeedback = () => {
    if (!id) return;
    const text = fbInput.trim();
    if (!text) {
      showErrorToastMessage("Write some progress feedback before saving.");
      return;
    }
    setFbSaving(true);
    dispatch(
      actions.saveAssessmentAction(
        id,
        { developmentProgressFeedback: text, updatedBy: loginId },
        (res: any) => {
          setFbSaving(false);
          if (res?.error) showErrorToastMessage("Failed to save feedback");
          else {
            showSuccessToastMessage("Progress feedback saved");
            loadCandidate();
          }
        }
      )
    );
  };

  // save both typed post-development numbers — doesn't touch the four assessment
  // factors, so it doesn't count as a reassessment.
  const saveEdit = () => {
    if (!id) return;
    // Close-gate: can't move to post-development without progress feedback. The
    // server enforces this too; this is the friendly, earlier message.
    if (!hasFeedback) {
      showErrorToastMessage(
        "Add progress feedback on the Development tab before recording post-development results."
      );
      return;
    }
    if (!validLevel(potInput) || !validLevel(perfInput)) {
      showErrorToastMessage("Potential and Performance must be 1, 2 or 3.");
      return;
    }
    setSaving(true);
    dispatch(
      actions.saveAssessmentAction(
        id,
        { postPotential: potInput || null, postPerformance: perfInput || null, updatedBy: loginId },
        (res: any) => {
          setSaving(false);
          if (res?.error) showErrorToastMessage("Failed to save");
          else {
            showSuccessToastMessage("Saved");
            setEditing(false);
            loadCandidate();
          }
        }
      )
    );
  };

  return (
    <MainCard>
      <div className="assessment-heading-button-wrapper">
        <div>
          <span className="seat-back" onClick={() => navigate("/succession-planning/development-plan")}>
            ← Development Plan
          </span>
          <h2 className="mt-2">{loading ? "Loading…" : empName(emp)}</h2>
          {!loading && candidate && (
            <p className="muted" style={{ marginTop: 4 }}>
              {emp?.employeeId ? `${emp.employeeId} · ` : ""}
              {emp?.designation || "-"}
              {seatLabel ? ` · succeeding ${seatLabel}` : ""}
            </p>
          )}
        </div>
        {!loading && candidate && (
          <div>
            <button
              className="btn add-submit-button buttonsize compact-btn"
              disabled={downloading}
              onClick={downloadReport}
            >
              {downloading ? "Preparing…" : "Download Report"}
            </button>
          </div>
        )}
      </div>

      {!loading && !candidate ? (
        <p className="empty-note mt-3">This successor could not be found.</p>
      ) : (
        <>
          <div className="cd-tabs mt-4">
            <button className={`cd-tab cd-pre ${stage === "pre" ? "active" : ""}`} onClick={() => setStage("pre")}>
              Pre-Development
            </button>
            <button className={`cd-tab cd-dev ${stage === "dev" ? "active" : ""}`} onClick={() => setStage("dev")}>
              Development
              <span className="cd-tab-count">{plans.length}</span>
            </button>
            <button className={`cd-tab cd-post ${stage === "post" ? "active" : ""}`} onClick={() => setStage("post")}>
              Post-Development
            </button>
          </div>

          {/* ---- 1. Pre-Development: the succession assessment (view only) ---- */}
          {stage === "pre" && (
          <div className="cd-panel mt-3">
            <div className="cd-note cd-note-pre">
              <b>Succession assessment.</b> The successor's four factors, as assessed. Record or
              update them from the <b>Post-Development</b> tab.
            </div>

            {isAssessed ? (
              <div className="cd-card mt-2">
                <div className="cd-card-head">Assessment</div>
                <div className="cd-detail-grid">
                  <div className="cd-field"><span className="cd-key">Readiness</span><span className="cd-value">{val(candidate?.readiness)}</span></div>
                  <div className="cd-field"><span className="cd-key">Performance</span><span className="cd-value">{val(candidate?.performance)}</span></div>
                  <div className="cd-field"><span className="cd-key">Potential</span><span className="cd-value">{val(candidate?.potential)}</span></div>
                  <div className="cd-field"><span className="cd-key">Risk of Loss</span><span className="cd-value">{val(candidate?.riskOfLoss)}</span></div>
                  <div className="cd-field"><span className="cd-key">KRA Score</span><span className="cd-value">{candidate?.weightedScoreSnapshot ?? <span className="muted">Not scored</span>}</span></div>
                </div>
                {candidate?.notes && (
                  <>
                    <div className="cd-card-sub">Notes</div>
                    <p style={{ margin: 0 }}>{candidate.notes}</p>
                  </>
                )}
              </div>
            ) : (
              <div className="cd-empty cd-empty-pre">
                <div className="cd-empty-title">Not assessed yet</div>
                <div className="cd-empty-sub">Use <b>Reassess</b> on the Post-Development tab to record the four factors.</div>
              </div>
            )}
          </div>
          )}

          {/* ---- 2. Development: the development plan ---- */}
          {stage === "dev" && (
          <div className="cd-panel mt-3">
            <div className="cd-note cd-note-dev">
              <b>Development plan.</b> The gaps and the actions to close them — pick a timeline and
              the target date fills in.
            </div>
            <DevelopmentPlanModal candidate={candidate} showForm onChange={loadPlans} />

            {/* Progress feedback — required before the successor can be moved to
                post-development, and shown on the dashboard against the employee. */}
            <div className="cd-card mt-4">
              <div className="cd-card-head">
                Progress Feedback
                {hasFeedback ? (
                  <span className="dp-status dp-completed" style={{ marginLeft: 8 }}>Recorded</span>
                ) : (
                  <span className="dp-status dp-not-started" style={{ marginLeft: 8 }}>Required</span>
                )}
              </div>
              <p className="muted" style={{ marginTop: 4 }}>
                How is this successor tracking against the plan? This must be filled before
                recording post-development results.
              </p>
              <textarea
                className="form-control"
                rows={4}
                placeholder="Describe progress against the development plan…"
                value={fbInput}
                onChange={(e) => setFbInput(e.target.value)}
              />
              {candidate?.developmentProgressFeedbackAt && (
                <p className="muted" style={{ marginTop: 4, fontSize: 12 }}>
                  Last updated {new Date(candidate.developmentProgressFeedbackAt).toLocaleDateString()}
                  {candidate?.developmentProgressFeedbackBy ? ` · ${candidate.developmentProgressFeedbackBy}` : ""}
                </p>
              )}
              <div className="mt-2">
                <button
                  className="btn add-submit-button buttonsize compact-btn"
                  disabled={fbSaving || fbInput.trim() === (candidate?.developmentProgressFeedback || "").trim()}
                  onClick={saveFeedback}
                >
                  {fbSaving ? "Saving…" : "Save Feedback"}
                </button>
              </div>
            </div>
          </div>
          )}

          {/* ---- 3. Post-Development: results row + performance entry ---- */}
          {stage === "post" && (
          <div className="cd-panel mt-3">
            <div className="cd-note cd-note-post">
              <b>Results.</b> Record the post-development Performance. The eye shows what each
              box/category means.
            </div>

            {!hasFeedback && (
              <div className="cd-note cd-note-dev" style={{ marginTop: 8 }}>
                <b>Progress feedback needed.</b> Add progress feedback on the{" "}
                <span className="seat-back" style={{ cursor: "pointer" }} onClick={() => setStage("dev")}>
                  Development tab
                </span>{" "}
                before recording post-development results.
              </div>
            )}

            <div className="cd-stage-head">
              <h5>Post-Development</h5>
              <div className="cd-head-actions">
                <button
                  className="cd-eye-btn"
                  title="What the 1–3 numbers mean"
                  onClick={() => setShowLegend((s) => !s)}
                >
                  <FiEye size={16} /> What the numbers mean
                </button>
                <Button title="Reassess" onClick={() => setShowAssess(true)} className="add-employee-btn" />
              </div>
            </div>

            {showLegend && (
              <div className="cd-legend mt-2">
                <div className="cd-legend-title">Performance / Potential scale</div>
                <div className="cd-legend-grid">
                  {SCALE.map((s) => (
                    <div className="cd-legend-row" key={s.n}>
                      <span className="cd-legend-box">{s.n}</span>
                      <span>{s.label}</span>
                    </div>
                  ))}
                </div>
                <div className="cd-legend-title mt-3">Box → Category</div>
                <div className="cd-legend-grid">
                  {LEGEND_ROWS.map((b) => (
                    <div className="cd-legend-row" key={b}>
                      <span className="cd-legend-box">Box {b}</span>
                      <span>{BOX_CATEGORY[b]}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {postBox && (
              <p className="muted mt-2">
                Current position: <b>{BOX_CATEGORY[postBox]}</b> (Box {postBox})
              </p>
            )}

            <div className="readable-table mt-2">
              <div className="table table-responsive">
                <table className="table-main table table-sm">
                  <thead className="table-head">
                    <tr className="table-head-row">
                      <th className="table-head-row">Designation</th>
                      <th className="table-head-row">Department</th>
                      <th className="table-head-row">Client</th>
                      <th className="table-head-row">Succeeding</th>
                      <th className="table-head-row">Status</th>
                      <th className="table-head-row">Potential</th>
                      <th className="table-head-row">Performance</th>
                      <th className="table-head-row">Action</th>
                    </tr>
                  </thead>
                  <tbody className="table-body">
                    <tr className="table-body-row">
                      <td>{val(emp?.designation)}</td>
                      <td>{val(emp?.department)}</td>
                      <td>{val(emp?.clientName)}</td>
                      <td>{val(seatLabel)}</td>
                      <td>
                        <span className={`dp-status dp-${devStatus.replace(/\s+/g, "-").toLowerCase()}`}>
                          {devStatus}
                        </span>
                      </td>
                      <td>
                        {editing ? (
                          <input
                            type="number"
                            min={1}
                            max={3}
                            className="form-control cd-perf-select"
                            value={potInput}
                            // placeholder="1-3"
                            onChange={(e) => setPotInput(e.target.value)}
                          />
                        ) : (
                          potNum ?? <span className="muted">-</span>
                        )}
                      </td>
                      <td>
                        {editing ? (
                          <input
                            type="number"
                            min={1}
                            max={3}
                            className="form-control cd-perf-select"
                            value={perfInput}
                            // placeholder="1-3"
                            onChange={(e) => setPerfInput(e.target.value)}
                          />
                        ) : (
                          perfNum ?? <span className="muted">-</span>
                        )}
                      </td>
                      <td>
                        {editing ? (
                          <div className="cd-row-actions">
                            <button className="btn add-submit-button buttonsize compact-btn" disabled={saving} onClick={saveEdit}>
                              {saving ? "Saving…" : "Save"}
                            </button>
                            <button className="btn cancel-btn buttonsize compact-btn" disabled={saving} onClick={cancelEdit}>
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            className="btn add-submit-button buttonsize compact-btn"
                            onClick={startEdit}
                            disabled={!hasFeedback}
                            title={hasFeedback ? "" : "Add progress feedback on the Development tab first"}
                          >
                            Edit
                          </button>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="muted mt-2">
                Performance here is the <b>post-development</b> score — the assessment on
                Pre-Development is the "before".
              </p>
            </div>
          </div>
          )}
        </>
      )}

      <ModalComponents
        show={showAssess}
        onHide={() => setShowAssess(false)}
        title="Reassess Candidate"
        modalBody={
          candidate ? (
            <AssessCandidate
              candidate={candidate}
              setShowAssess={setShowAssess}
              setAssessData={() => {}}
              onSaved={loadCandidate}
            />
          ) : null
        }
        className="modal-md"
      />
    </MainCard>
  );
};

export default CandidateDevelopment;
